/**
 * imports
 */
import { createApp } from "vue";
import store from "@/store";
import { apolloProvider } from "@/graphql/vue-apollo";
import VueResizeObserver from "vue-resize-observer";
import { toCurrency, pluralize } from "@/filters";
import { Vue3Mq } from "vue3-mq";
import * as Sentry from "@sentry/vue";
import { BrowserTracing } from "@sentry/tracing";
import Donut from "vue-css-donut-chart";
import "vue-css-donut-chart/dist/vcdonut.css";
import { withoutGraphQLFetch } from "apollo-link-sentry";
import { getFeatureToggles } from "@/helpers/featureToggles.js";
import clickOutside from "@/helpers/click-outside";

require("@/myfont.font.js");

/**
 * create vue instance function
 */

const regions = {
  "www.ratandboa.com": "UK",
  "eur.ratandboa.com": "EU",
  "us.ratandboa.com": "US",
  "aus.ratandboa.com": "AU",
};

export default (component, router = null, plugins = []) => {
  const createVueApp = () => {
    const app = createApp({
      name: "BaseApp",
      created() {
        getFeatureToggles();
      },
      mounted() {
        const actions = [
          "app/getShopInfo",
          "app/getUserCountry",
          "auth/fetchCustomer",
          "auth/checkAccessToken",
          "app/getUserPreferences",
          "app/getRecentlyViewedProducts",
          "app/getNavigationLinks",
          "app/getFooterLeftLinks",
          "app/getFooterRightLinks",
          "app/storeUTMDetails",
          "product/getWishlistProducts",
          "cart/fetchCart",
          "cart/applyDiscount",
          "auth/getCustomerDetails",
        ];

        actions.forEach((action) => store.dispatch(action));

        Sentry.setTag("isLoggedIn", store.getters["auth/isLoggedIn"]);
        Sentry.setTag("region", regions[window.location.hostname]);
      },
    });

    /**
     * vue filters
     * are no longer supported in vue 3, use globalProps instead
     */
    app.config.globalProperties.$filters = {
      toCurrency,
      pluralize,
    };
    app.config.globalProperties.window = window;

    /**
     * vue directives
     */
    app.directive("click-outside", clickOutside);

    /**
     * vue views
     * auto-import all vue components
     */
    importAll(require.context("./components/layout/", true, /\.(vue|js)$/));

    function importAll(r) {
      r.keys().forEach((key) => {
        const view = r(key).default;
        const name =
          view.name ||
          key
            .replace(/\.(\/|vue|js)/g, "")
            .replace(/(\/|-|_|\s)\w/g, (match) => match.slice(1).toUpperCase());
        app.component(name, view);
      });
    }

    if (component) {
      const name =
        component.name ||
        component.__file
          .split("/")
          .at(-1)
          .replace(/\.(\/|vue|js)/g, "")
          .replace(/(\/|-|_|\s)\w/g, (match) => match.slice(1).toUpperCase());
      app.component(name, component);
    }

    if (router) {
      app.use(router);
    }

    app.use(VueResizeObserver);
    app.use(apolloProvider);
    app.use(store);
    app.use(Vue3Mq, {
      breakpoints: {
        xxs: 0,
        xs: 355,
        sm: 429,
        md: 821,
        lg: 992,
        xl: 1200,
        xxl: 1400,
      },
    });
    app.use(Donut);

    plugins.forEach((a) => {
      app.use(a.plugin, a.options);
    });

    /**
     * Provides
     */

    app.provide(
      "isPlaywright",
      localStorage.getItem("isPlaywright") === "true"
    );

    /**
     * Sentry
     */

    app.config.errorHandler = function (error, vm, info) {
      console.error(error, vm, info);
    };

    const integrations = [];

    if (router) {
      integrations.push(
        new BrowserTracing({
          routingInstrumentation: Sentry.vueRouterInstrumentation(router),
        })
      );
    }

    if (process.env.NODE_ENV === "production") {
      Sentry.init({
        app,
        environment: process.env.NODE_ENV,
        dsn: "https://1e2ef08c78ab4e25bca8a83de60b8264@o1219741.ingest.sentry.io/6362258",
        integrations,
        debug: false,
        attachProps: true, // Includes all Vue components' props with the events.
        logErrors: true, // Decides whether SDK should call Vue's original logError function as well.
        trackComponents: true, // Decides whether to track components by hooking into its lifecycle methods.
        normalizeDepth: 5,
        sampleRate: 0.1,
        tracesSampleRate: 0.03,
        allowUrls: [/(ratandboa|shopify)\.com/],
        beforeBreadcrumb: withoutGraphQLFetch((breadcrumb, hint) => {
          if (breadcrumb.category === "fetch") {
            const data = {
              requestUrl: hint.input[0],
              requestBody: hint.input[1].body,
            };
            return { ...breadcrumb, data };
          }
          return breadcrumb;
        }),
        ignoreErrors: [
          // Random plugins/extensions
          "top.GLOBALS",
          // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
          "originalCreateNotification",
          "canvas.contentDocument",
          "MyApp_RemoveAllHighlights",
          "http://tt.epicplay.com",
          "Can't find variable: ZiteReader",
          "jigsaw is not defined",
          "ComboSearch is not defined",
          "http://loading.retry.widdit.com/",
          "atomicFindClose",
          // Facebook borked
          "fb_xd_fragment",
          // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
          // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
          "bmi_SafeAddOnload",
          "EBCallBackMessageReceived",
          // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
          "conduitPage",
          // Generic error code from errors outside the security sandbox
          // You can delete this if using raven.js > 1.0, which ignores these automatically.
          "Script error.",
        ],
      });
    }

    return app;
  };

  /**
   * create and mount vue instance(s)
   */
  const appElement = document.querySelector("#app");

  if (appElement) {
    createVueApp().mount(appElement);
  } else {
    const vueElements = document.querySelectorAll("[vue]");
    if (vueElements) vueElements.forEach((el) => createVueApp().mount(el));
  }

  /**
   * fixes for Shopify sections
   * 1. properly render vue components on user insert in the theme editor
   * 2. reload the current page to rerender async inserted sections with vue components
   *
   * add the 'vue' keyword to the section's wrapper classes if the section uses any vue functionality e.g.:
   * {% schema %}
   * {
   *   "class": "vue-section"
   * }
   * {% endschema %}
   */
  if (Shopify.designMode) {
    document.addEventListener("shopify:section:load", (event) => {
      if (event.target.classList.value.includes("vue")) {
        createVueApp().mount(event.target);
      }
    });
  } else if (!Shopify.designMode && process.env.NODE_ENV === "development") {
    new MutationObserver((mutationsList) => {
      mutationsList.forEach((record) => {
        const vue = Array.from(record.addedNodes).find(
          (node) => node.classList && node.classList.value.includes("vue")
        );
        if (vue) window.location.reload();
      });
    }).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
};
